import * as R from "ramda";

import { getSecondsFromMMSS } from "../../utils/date";
import * as strings from "../../constants/strings";
import {
  INVALID_POSTCODE,
  INVALID_STOP_TIME,
  SATURDAY_SERVICE_SHORT,
} from "../../constants/strings";
import {
  removalRequestTypes,
  requestTypes,
  responseTypes,
  requestIconType,
} from "../../enums/requestTypes";
import { joinArrayToString } from "../../utils/string";
import FILTERS from "../../enums/RouteDetailFilters";
import palette from "../../components/styles/palette";
import formatPostcode from "../../utils/formatPostcode";
import getParcelNumber from "../../utils/getParcelNumber";
import { TIME_FORMAT, POSTCODE } from "../../constants/regex";
import { getFilterFn } from "../../models/routeDetail";
import {
  isBreakStop,
  isCollectionStop,
  getServiceWindow,
  formatDisplayNumber,
} from "../../models/stop";
import { isAddParcelRequest } from "../../models/request";
import ragStatusColors from "../../enums/ragStatusColors";

export const findStopPostcode = (stop) => {
  if (stop.parcel?.length) {
    const firstParcel = stop.parcel.find((p) => +p.parcelIndex === 1);
    const { postcode } = (firstParcel && firstParcel.address) || {};
    return postcode;
  }
};

export const isFilterDisabled = (filterId, stops) => {
  const selectedStops = stops.filter((stop) => stop.selected);
  const nextStops = selectedStops.length ? selectedStops : stops;
  return !nextStops.filter((stop) => getFilterFn(filterId)(stop, nextStops))
    .length;
};

export const isPendingStop = ({ pendingStop = false }) => pendingStop;

export const getExceptionEntry = (exception) => {
  const entries = {
    1: strings.POSTCODE,
    2: strings.SERVICE,
    3: strings.DUTY,
    6: strings.RANGE,
    7: strings.LEGAL_DUTY,
  };

  return entries[exception] || "";
};

export const isSaturdayService = (stop) =>
  stop?.parcel?.some((parcel) => parcel.saturdayService);

export const isSundayService = (stop) =>
  stop?.parcel?.some((parcel) => parcel.sundayService);

const getServiceLabelForStops = (stop) => {
  if (isSaturdayService(stop)) {
    return strings.SATURDAY_SERVICE_SHORT;
  }

  if (isSundayService(stop)) {
    return strings.SUNDAY_SERVICE_SHORT;
  }

  return "";
};

const getServiceLabelForParcel = (parcel = {}) => {
  if (parcel.saturdayService) {
    return strings.SATURDAY_SERVICE_SHORT;
  }

  if (parcel.sundayService) {
    return strings.SUNDAY_SERVICE_SHORT;
  }

  return "";
};

export const isRemoveRequest = ({ requestType }) =>
  !!removalRequestTypes.includes(requestType);

export const getRequestReason = ({ requestType, requestReason }) =>
  requestType === requestTypes.AMEND_PICKUP_PARCELS
    ? `Pickup - ${requestReason}`
    : requestReason;

export const isRequestHasResponse = ({ response }) => !R.isNil(response);

export const isDeferredRequest = ({ response }) =>
  !!response && response.responseType === responseTypes.DEFER;

export const getStopRequestType = (stopRequests) => {
  if (!stopRequests.length) return {};

  const isAllDeferred = stopRequests.every(isDeferredRequest);
  if (isAllDeferred) {
    return {
      type: requestIconType.DEFFER,
      count: stopRequests.length,
    };
  }

  const isFirstStopRequestRemoveRequest = isRemoveRequest(R.head(stopRequests));

  const isMixed = stopRequests.slice(1).some((request) => {
    if (isFirstStopRequestRemoveRequest) {
      return requestTypes.ADD_PARCEL === request.requestType;
    } else {
      return [
        requestTypes.REMOVE_PARCEL,
        requestTypes.AMEND_PICKUP_PARCELS,
      ].includes(request.requestType);
    }
  });

  if (isMixed) {
    return {
      type: requestIconType.MIXED,
      count: stopRequests.length,
    };
  }

  if (isFirstStopRequestRemoveRequest) {
    return {
      type: requestIconType.REMOVE,
      count: stopRequests.length,
    };
  }

  return {
    type: requestIconType.ADD,
    count: stopRequests.length,
  };
};

export const getRagStatusColor = (status) => {
  const ragStatusesMap = {
    R: palette.dpdRed,
    A: palette.statuses.statusAmber,
    G: palette.statuses.statusGreen,
    B: palette.statuses.statusBlue,
  };

  return ragStatusesMap[status];
};

export const getCadScannedStatusColor = (value, { parcelStatus }) => {
  if (value) {
    return palette.statuses.statusGreen;
  }
  return parcelStatus === "B" ? palette.statuses.statusBlue : palette.dpdRed;
};

export const getCadStatusTextColor = (value, { parcelStatus }) =>
  value ? "green" : parcelStatus === "B" ? "blue" : "red";

export const getRagStatusTextColor = (status) =>
  status ? ragStatusColors[status] : "no-icon";

export const getStatusTextColor = (value) => {
  const color = value?.perishable
    ? "blue"
    : value?.vipDelivery
    ? "red"
    : "no-icon";

  return color;
};

export const gramsToKg = (weight) => (isNaN(weight) ? 0 : weight / 1000);

export const formatToKg = (weight) => `${gramsToKg(weight)} ${strings.KG}`;

export const isHighValueParcel = ({ parcel = [] }) =>
  parcel.some((item) => item.highValue);

export const isSmallsToteParcel = ({ parcel = [] }) =>
  parcel.some((item) => item.smallsToteParcel);

export const generateParcelFromRequest = (requests = [], stop) => {
  let maxParcelIndex = stop.parcel
    ? Math.max(...stop.parcel.map((p) => p.parcelIndex))
    : 1;
  return requests.map((request) => ({
    ...request,
    cadScanned: request.cadScan,
    parcelNumber: getParcelNumber(request.labelNumber),
    priority: 0,
    actualWeight: false,
    weight: 0,
    displayServiceWindow: true,
    parcelIndex: maxParcelIndex++,
    displayNumber: stop.displayNumber,
    sequenceNumber: stop.sequenceNumber,
  }));
};

export const isRequestedParcelInStop = (request, stop) =>
  (stop.parcel || []).find((p) => p.parcelCode === request.parcelCode);

export const getAPRequests = (stop) =>
  stop.requests
    ? stop.requests.filter(
        (req) => isAddParcelRequest(req) && !isRequestedParcelInStop(req, stop)
      )
    : [];

export const prepareTableData = (filteredStops) =>
  filteredStops.map((stop) => {
    const commonFields = {
      stopTime: stop.stopTime || "",
      eta: stop.eta,
      type: stop.stopTypeDescription,
      selected: stop.selected,
      stopNumber: stop.stopNumber,
      sequenceNumber: stop.sequenceNumber,
      window: getServiceWindow(stop),
      stopId: stop.stopId,
    };
    if (isBreakStop(stop)) {
      return {
        address: strings.LUNCH_BREAK.toUpperCase(),
        displayNumber: stop.displayNumber,
        sequenceNumber: stop.sequenceNumber,
        ...commonFields,
      };
    }

    const APRequests = getAPRequests(stop);
    const additionalParcels = generateParcelFromRequest(APRequests, stop);
    const parcelData =
      !stop.parcel && !APRequests.length
        ? []
        : [...stop.parcel, ...additionalParcels];

    return {
      key: `${stop.stopNumber}_${stop.stopType}`,
      service: {
        perishable: stop.perishable,
        vipDelivery: stop.vipDelivery,
        serviceLabel: getServiceLabelForStops(stop),
      },
      displayNumber: formatDisplayNumber(
        stop.displayNumber || stop.sequenceNumber
      ),
      priority: stop.priority,
      address: joinArrayToString([
        stop.stopAddress?.contactName,
        stop.stopAddress?.address1,
      ]),
      postcode: formatPostcode(stop.stopAddress?.postcode),
      ragStatus: !isCollectionStop(stop) && stop.ragStatus,
      totalParcels: stop.totalParcels,
      consolidated: stop.consolidated,
      totalCadParcels: stop.totalCadParcels,
      totalSmalls: stop.totalSmalls,
      totalLoaded: stop.totalLoaded,
      opportunityTime: stop.opportunityTime,
      exception: stop.exception,
      nonCompatibles:
        (Array.isArray(stop?.nonCompatibles) &&
          joinArrayToString(stop?.nonCompatibles)) ||
        stop?.nonCompatibles,
      weight: stop.weight,
      chute: stop.chute,
      requests: stop.requests,
      pendingStop: stop.pendingStop,
      highValue: isHighValueParcel({ parcel: parcelData }),
      smallsToteParcel: isSmallsToteParcel({ parcel: parcelData }),
      ...commonFields,
    };
  });

export const countTotalByField = (data, key) =>
  data.reduce((total, row) => (row[key] ? total + row[key] : total), 0);

export const getServiceSortIndex = (item) => {
  const { service = {} } = item;

  if (service.perishable) {
    return 0;
  } else if (service.vipDelivery) {
    return 1;
  } else if (service.serviceLabel) {
    return service.serviceLabel === SATURDAY_SERVICE_SHORT ? 2 : 3;
  }

  return 4;
};

export const isParcelDetailAvailable = (selectedStops) =>
  selectedStops.length === 1 &&
  !isCollectionStop(selectedStops[0]) &&
  !isBreakStop(selectedStops[0]);

export const defaultOrderFn = (a, b, orderBy) => {
  //TODO make orderBy as first param and wrap with curry
  const aVal = a[orderBy];
  const bVal = b[orderBy];

  if (aVal === bVal) {
    return 0;
  } else if (typeof aVal !== "number" && !aVal) {
    return 1;
  } else if (typeof bVal !== "number" && !bVal) {
    return -1;
  }

  const numericResult = aVal - bVal;

  if (!isNaN(numericResult)) {
    return numericResult;
  }

  return (aVal || "").localeCompare(bVal || "");
};

export const orderExceptions = (a, b) => {
  const aException = getExceptionEntry(a.exception);
  const bException = getExceptionEntry(b.exception);

  if (!aException) {
    return 1;
  } else if (!bException) {
    return -1;
  }

  return aException.localeCompare(bException);
};

export const createNumericOrderFn = (fieldName) => (a, b) => {
  if (a[fieldName] === 0) {
    return 1;
  } else if (b[fieldName] === 0) {
    return -1;
  }

  return a[fieldName] - b[fieldName];
};

export const getNextRouteFilters = (filters, id) => {
  const isApplied = filters.includes(id);
  let result;

  if (isApplied) {
    const nextFilters = filters.filter((f) => f !== id);

    result =
      nextFilters.length === 1 && nextFilters[0] === FILTERS.SHOW_IN_ROUTE
        ? []
        : nextFilters;
  } else {
    result = [...filters, id];
  }

  return result;
};

export const getParcelRequests = (stop, parcel) =>
  stop?.requests.filter((r) => r.parcelCode === parcel.parcelCode);

export const isAddRequest = (stop, parcel) => {
  if (!stop?.requests) {
    return false;
  }
  const request = getParcelRequests(stop, parcel);
  return isAddParcelRequest(request[0]);
};

export const parcelTableDataMapper = (parcels, stop, selectedParcels = []) =>
  parcels
    .map((parcel) => ({
      ...parcel,
      address: R.is(String, parcel.address)
        ? parcel.address
        : joinArrayToString([
            parcel.address.contactName,
            parcel.address.address1,
            parcel.address.address2,
            parcel.address.city,
            formatPostcode(parcel.address.postcode),
          ]).toUpperCase(),
      king: parcel.parcelIndex === 1 && !isAddRequest(stop, parcel),
      window: getServiceWindow(parcel),
      service: {
        perishable: parcel.perishable,
        vipDelivery: parcel.vipDelivery,
        serviceLabel: getServiceLabelForParcel(parcel),
      },
      weight: {
        actualWeight: parcel.actualWeight,
        weight: parcel.weight,
      },
      requests: getParcelRequests(stop, parcel),
      selected: !!selectedParcels[parcel.parcelCode],
      totalCadParcels: stop?.totalCadParcels,
      exception: stop?.exception,
    }))
    .sort((a, b) => defaultOrderFn(a, b, "parcelIndex"));

export const prepareParcels = (stop, selectedParcels) => {
  if (!stop) {
    return [];
  }

  const APRequests = getAPRequests(stop);

  if (!stop.parcel && !APRequests.length) {
    return [];
  }
  const additionalParcels = generateParcelFromRequest(APRequests, stop);

  return parcelTableDataMapper(
    [...stop.parcel, ...additionalParcels],
    stop,
    selectedParcels
  );
};

export const validateStopTime = (time) =>
  TIME_FORMAT.test(time) ? null : INVALID_STOP_TIME;

export const validatePostcode = (postcode) =>
  POSTCODE.test(postcode) ? null : INVALID_POSTCODE;

export const getStopByNumber = (stops, stopNumber) =>
  stops.find((stop) => stop.stopNumber === stopNumber);

export const updateStopTime = (stop, time) => ({
  ...stop,
  stopTime: getSecondsFromMMSS(time),
});

export const updateStopPostcodeData = (stop, postcodeData) => ({
  ...stop,
  stopAddress: {
    ...stop.stopAddress,
    ...postcodeData,
    postcodeChanged: true,
  },
});

export const resetStopDataChanged = (stops = []) =>
  stops.map((stop) => {
    if (!isBreakStop(stop)) {
      return stop.parcel
        ? {
            ...stop,
            stopAddress: {
              ...stop.stopAddress,
              postcodeChanged: false,
            },
            parcel: stop.parcel.map((item) =>
              R.omit(
                [
                  "originalNetwork",
                  "originalServiceStartTime",
                  "originalServiceEndTime",
                  "serviceDowngraded",
                ],
                item
              )
            ),
          }
        : {
            ...stop,
            stopAddress: {
              ...stop.stopAddress,
              postcodeChanged: false,
            },
          };
    }
    return stop;
  });

export const isPostcodeMatched = (currentStop, stopToMatch) =>
  formatPostcode(currentStop.stopAddress?.postcode) ===
  formatPostcode(stopToMatch.stopAddress?.postcode);

export const getDropdownListStops = (leadStop, stops, matchPostcodes) =>
  matchPostcodes
    ? stops.filter((stop) => isPostcodeMatched(leadStop, stop))
    : stops;
